import type { ComparisonStatus, ContentResourceComparison, FieldDiff } from '@/lib/compare-utils';
import { COMPARABLE_FIELDS, formatValueForDisplay } from '@/lib/compare-utils';
import { cn } from '@/lib/utils';
import { AlertCircle, CheckCircle2, ChevronDown, ChevronRight, ExternalLink, MinusCircle, XCircle } from 'lucide-react';
import { useState } from 'react';

interface DiffViewerProps {
    comparisons: ContentResourceComparison[];
    leftLabel: string;
    rightLabel: string;
    leftCode: string;
    rightCode: string;
    filter?: ComparisonStatus | 'all';
}

const statusConfig: Record<ComparisonStatus, { icon: typeof CheckCircle2; color: string; label: string; bgColor: string }> = {
    match: {
        icon: CheckCircle2,
        color: 'text-emerald-600',
        label: 'Match',
        bgColor: 'bg-emerald-50 border-emerald-200',
    },
    difference: {
        icon: XCircle,
        color: 'text-amber-600',
        label: 'Difference',
        bgColor: 'bg-amber-50 border-amber-200',
    },
    missing_left: {
        icon: MinusCircle,
        color: 'text-rose-600',
        label: 'Missing in Left',
        bgColor: 'bg-rose-50 border-rose-200',
    },
    missing_right: {
        icon: AlertCircle,
        color: 'text-blue-600',
        label: 'Missing in Right',
        bgColor: 'bg-blue-50 border-blue-200',
    },
};

function MissingResourceDisplay({ resource, label }: { resource: ContentResourceComparison['left']; label: string }) {
    if (!resource) return null;

    return (
        <div className="p-4">
            <div className="mb-3 text-sm text-zinc-600">
                This content resource only exists in <span className="font-medium">{label}</span>:
            </div>
            <div className="space-y-2">
                {COMPARABLE_FIELDS.map(field => {
                    const value = field === 'payload' ? resource.payload : resource[field as keyof typeof resource];
                    const displayValue = formatValueForDisplay(value);
                    const isLongContent = displayValue.length > 100 || displayValue.includes('\n');

                    return (
                        <div
                            key={field}
                            className="rounded border border-zinc-200 bg-zinc-50 p-2"
                        >
                            <div className="mb-1 font-mono text-xs font-medium text-zinc-500">{field}</div>
                            <pre
                                className={cn(
                                    'whitespace-pre-wrap break-all font-mono text-xs text-zinc-700',
                                    isLongContent && 'max-h-32 overflow-auto',
                                )}
                            >
                                {displayValue}
                            </pre>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function FieldDiffRow({ diff, leftLabel, rightLabel }: { diff: FieldDiff; leftLabel: string; rightLabel: string }) {
    const [expanded, setExpanded] = useState(false);
    const leftStr = formatValueForDisplay(diff.leftValue);
    const rightStr = formatValueForDisplay(diff.rightValue);
    const isLongContent = leftStr.length > 100 || rightStr.length > 100;
    const isMultiline = leftStr.includes('\n') || rightStr.includes('\n');

    return (
        <div className={cn('border-b border-zinc-100 last:border-b-0', !diff.isEqual && 'bg-amber-50/50')}>
            <div
                className={cn('flex items-center gap-2 px-3 py-2', (isLongContent || isMultiline) && 'cursor-pointer hover:bg-zinc-50')}
                onClick={() => (isLongContent || isMultiline) && setExpanded(!expanded)}
            >
                {(isLongContent || isMultiline) && (
                    <span className="text-zinc-400">{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
                )}
                <span className="w-28 shrink-0 font-mono text-xs text-zinc-500">{diff.field}</span>
                <span className={cn('ml-auto text-xs font-medium', diff.isEqual ? 'text-emerald-600' : 'text-amber-600')}>
                    {diff.isEqual ? 'Equal' : 'Different'}
                </span>
            </div>

            {(expanded || (!isLongContent && !isMultiline)) && (
                <div className="grid grid-cols-2 gap-2 px-3 pb-2">
                    <div className="rounded border border-zinc-200 bg-zinc-50 p-2">
                        <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-zinc-400">{leftLabel}</div>
                        <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all font-mono text-xs text-zinc-700">
                            {leftStr}
                        </pre>
                    </div>
                    <div className="rounded border border-zinc-200 bg-zinc-50 p-2">
                        <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-zinc-400">{rightLabel}</div>
                        <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all font-mono text-xs text-zinc-700">
                            {rightStr}
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
}

function ComparisonCard({
    comparison,
    leftLabel,
    rightLabel,
    leftCode,
    rightCode,
}: {
    comparison: ContentResourceComparison;
    leftLabel: string;
    rightLabel: string;
    leftCode: string;
    rightCode: string;
}) {
    const [expanded, setExpanded] = useState(false);
    const config = statusConfig[comparison.status];
    const StatusIcon = config.icon;

    const resourceType = comparison.left?.resource_type || comparison.right?.resource_type || 'Unknown';
    const diffCount = comparison.fieldDiffs.filter(d => !d.isEqual).length;

    const getViewUrl = (code: string, crId: number) =>
        `${import.meta.env.VITE_ZYBOOKS_URL}/zybook/${code}/chapter/${comparison.chapterNumber}/section/${comparison.sectionNumber}?content_resource_id=${crId}`;

    return (
        <div className={cn('rounded-lg border', config.bgColor)}>
            <div
                className="flex cursor-pointer items-center gap-3 px-4 py-3"
                onClick={() => setExpanded(!expanded)}
            >
                <span className="text-zinc-400">{expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
                <StatusIcon
                    size={18}
                    className={config.color}
                />
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-zinc-800">{resourceType}</span>
                        <span className="rounded bg-zinc-200 px-1.5 py-0.5 font-mono text-[10px] text-zinc-600">
                            pos: {comparison.positionKey}
                        </span>
                    </div>
                    <div className="text-xs text-zinc-500">
                        Ch {comparison.chapterNumber}.{comparison.sectionNumber} • CR #{comparison.ordering}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {comparison.left && (
                        <a
                            href={getViewUrl(leftCode, comparison.left.content_resource_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="flex items-center gap-1 rounded border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-100"
                            title={`View in ${leftLabel}`}
                        >
                            <ExternalLink size={12} />
                            {leftLabel}
                        </a>
                    )}
                    {comparison.right && (
                        <a
                            href={getViewUrl(rightCode, comparison.right.content_resource_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="flex items-center gap-1 rounded border border-teal-200 bg-teal-50 px-2 py-1 text-xs font-medium text-teal-600 hover:bg-teal-100"
                            title={`View in ${rightLabel}`}
                        >
                            <ExternalLink size={12} />
                            {rightLabel}
                        </a>
                    )}
                </div>
                <div className="text-right">
                    <span className={cn('text-sm font-medium', config.color)}>{config.label}</span>
                    {comparison.status === 'difference' && (
                        <div className="text-xs text-zinc-500">
                            {diffCount} field{diffCount !== 1 ? 's' : ''} differ
                        </div>
                    )}
                </div>
            </div>

            {expanded && (
                <div className="border-t border-zinc-200 bg-white">
                    {comparison.status === 'missing_left' && (
                        <MissingResourceDisplay
                            resource={comparison.right}
                            label={rightLabel}
                        />
                    )}
                    {comparison.status === 'missing_right' && (
                        <MissingResourceDisplay
                            resource={comparison.left}
                            label={leftLabel}
                        />
                    )}
                    {(comparison.status === 'match' || comparison.status === 'difference') &&
                        COMPARABLE_FIELDS.map(field => {
                            const diff = comparison.fieldDiffs.find(d => d.field === field);
                            if (!diff) return null;
                            return (
                                <FieldDiffRow
                                    key={field}
                                    diff={diff}
                                    leftLabel={leftLabel}
                                    rightLabel={rightLabel}
                                />
                            );
                        })}
                </div>
            )}
        </div>
    );
}

export function DiffViewer({ comparisons, leftLabel, rightLabel, leftCode, rightCode, filter = 'all' }: DiffViewerProps) {
    const filteredComparisons = filter === 'all' ? comparisons : comparisons.filter(c => c.status === filter);

    if (filteredComparisons.length === 0) {
        return (
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-8 text-center text-zinc-500">
                No content resources to display
                {filter !== 'all' && ` with status "${filter}"`}
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {filteredComparisons.map(comparison => (
                <ComparisonCard
                    key={comparison.positionKey}
                    comparison={comparison}
                    leftLabel={leftLabel}
                    rightLabel={rightLabel}
                    leftCode={leftCode}
                    rightCode={rightCode}
                />
            ))}
        </div>
    );
}

export default DiffViewer;
